import { useContext, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Tooltip,
  Pagination,
  IconButton,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";
import api, { downloadFile } from "../../../BrandOnBoarding/api";
import { AuthContext, getSharedReportIdentity } from "../../../context/auth-context";
import { DiscoveryContext } from "../discovery-context";
import { CategoryAnalysisContext } from "../../CategoryAnalysis/category-analysis-context";
import SummaryReportModal from "../SummaryReportModal";
import HTMLReportHistoryItem from "./HTMLReportHistoryItem";
import HTMLReportViewer from "./HTMLReportViewer";
import useHTMLReportHistory from "./useHTMLReportHistory";
import CustomizedTables from "../../../Component/UI/Table/Table";
import classes from "./HTMLReportTab.module.css";
import { ENTITY_LABELS } from "../../../utils/labelMapping";


const PAGE_SIZE = 5;

const CATEGORY_REPORT_TYPE_TABS = [
  { label: "Category Teaser", flag: "categoryTeaser", description: "A high-level snapshot of the category." },
  { label: "Category Brief", flag: "categoryBrief", description: "Summarised category insights and brand positioning." },
  { label: "Category Analysis Report", flag: "categoryAnalysis", description: "Full in-depth category analysis report." },
];

// Keep in step with EMPLOYER_CATEGORY_USER_IDS in the backend (employer-category-report.js).
const EMPLOYER_CATEGORY_USER_IDS = [238];

const TABLE_HEADERS = [
  // { id: "no", name: "#", sorting: false },
  { id: "category", name: "Category", sorting: false },
  { id: "reportType", name: "Report Type", sorting: false },
  { id: "type", name: "Type", sorting: false },
  { id: "llm", name: "LLM", sorting: false },
  { id: "markets", name: "Markets", sorting: false },
  { id: "personas", name: "Personas", sorting: false },
  { id: "date", name: "Date", sorting: false },
  { id: "status", name: "Status", sorting: false },
  { id: "actions", name: "Actions", sorting: false },
];

/**
 * HTMLReportTab — rendered as the "Reporting" tab in BrandScore.
 */
export default function HTMLReportTab({
  brandName,
  categories = [],
  llms = [],
  markets = [],
  personas = [],
  transactionId,
  flag = 'brand_audit',
  hideGenerateButton = false,
}) {
  const { user, selectedCustomerId } = useContext(AuthContext);
  const brandAuditDiscoveryCtx = useContext(DiscoveryContext);
  const categoryAnalysisCtx = useContext(CategoryAnalysisContext);
  const isCategoryAnalysisFlag = ['categoryAnalysis', 'categoryTeaser', 'categoryBrief'].includes(flag);
  const discoveryCtx = isCategoryAnalysisFlag ? categoryAnalysisCtx : brandAuditDiscoveryCtx;
  const userId = user?.user_role?.user_id;
  // "Category Analysis Report" (the full 33-slide deck) is gated per-user while it rolls
  // out — shown but disabled (not removed) until the flag is on. Category Teaser /
  // Category Brief stay available regardless of this flag.
  // Employer Category report users (same ids as the backend allowlist) get only the
  // full Category Analysis Report: Teaser / Brief are disabled and Analysis is enabled.
  const isEmployerCategoryUser = EMPLOYER_CATEGORY_USER_IDS.includes(Number(userId));
  const categoryReportTypeTabs = CATEGORY_REPORT_TYPE_TABS.map((t) => {
    if (isEmployerCategoryUser) return { ...t, disabled: t.flag !== 'categoryAnalysis' };
    return t.flag === 'categoryAnalysis'
      ? { ...t, disabled: !user?.is_category_analysis_report_enabled }
      : t;
  });
  const discoveryType = discoveryCtx.type || "brand";
  const isProductAudit = discoveryType === "product";

  // Category Analysis supports selective metric runs — narrow the JSON export's metric
  // dropdown to only what actually ran for this transaction. Other flags (brand audit) always
  // run every metric, so leave their export dropdown showing all options (availableMetricKeys=null).
  const caSelectedMetrics = categoryAnalysisCtx.state?.selectedMetrics;
  const availableMetricKeys = (isCategoryAnalysisFlag && caSelectedMetrics?.length > 0)
    ? caSelectedMetrics.map((m) => ({
        rank: "includeVisibility",
        sentiment: "includeSentiment",
        categoryDimension: "includePurchaseCriteria",
        coreAttribute: discoveryType === "product" ? "includeProductDimension" : "includeBrandPillar",
        signalStrength: "includePositiveNegativeAttribute",
      }[m])).filter(Boolean)
    : null;

  const [showModal, setShowModal] = useState(false);
  const [generating, setGenerating] = useState(0); // count of in-flight HTML report generation requests
  const [generatingJson, setGeneratingJson] = useState(false);
  const [page, setPage] = useState(1);

  // Inline viewer state
  const [inlineViewJobId, setInlineViewJobId] = useState(null);
  const [inlineHtml, setInlineHtml] = useState("");
  const [inlineLoading, setInlineLoading] = useState(false);

  // Duplicate confirmation dialog
  const [duplicateDialog, setDuplicateDialog] = useState({
    open: false,
    existingJob: null,
    pendingSelections: null,
    pendingType: null, // "html" | "comprehensive"
  });

  const {
    jobs,
    loading: historyLoading,
    addJob,
    findCompletedJob,
    downloadReport,
    viewReport,
    cancelJob,
    refreshHistory,
  } = useHTMLReportHistory({ userId, transactionId });

  // ── Pagination ────────────────────────────────────────────────────────────

  const totalPages = Math.ceil(jobs.length / PAGE_SIZE);
  const pagedJobs = jobs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handlePageChange = (_, newPage) => setPage(newPage);

  // ── Inline view ───────────────────────────────────────────────────────────

  const handleViewInline = useCallback(async (jobId) => {
    setInlineLoading(true);
    setInlineViewJobId(jobId);
    setInlineHtml("");
    try {
      const response = await api.get(`/llm-reporting/download/${jobId}`, {
        responseType: "text",
      });
      setInlineHtml(response.data || "");
    } catch {
      toast.error("Failed to load report. Please try again.");
      setInlineViewJobId(null);
    } finally {
      setInlineLoading(false);
    }
  }, []);

  const handleBackToHistory = () => {
    setInlineViewJobId(null);
    setInlineHtml("");
  };

  // ── Viewer save callbacks ─────────────────────────────────────────────────

  // Replace the HTML for the existing job on the backend, then refresh history
  const handleSaveUpdate = useCallback(
    async (newHtml) => {
      await api.patch(`/llm-reporting/html-report/${inlineViewJobId}`, {
        html: newHtml,
        userId,
        transactionId,
      });
      await refreshHistory();
      // Update what the viewer is showing
      setInlineHtml(newHtml);
    },
    [inlineViewJobId, userId, transactionId, refreshHistory]
  );

  // Save as a brand-new report entry
  const handleSaveNew = useCallback(
    async (newHtml) => {
      await api.post("/llm-reporting/html-report", {
        html: newHtml,
        userId,
        transactionId,
        sourceJobId: inlineViewJobId,
      });
      await refreshHistory();
      setPage(1);
    },
    [inlineViewJobId, userId, transactionId, refreshHistory]
  );

  // ── Open in new tab ───────────────────────────────────────────────────────

  const handleViewInNewTab = useCallback(
    async (jobId) => {
      try {
        await viewReport(jobId);
      } catch (err) {
        if (err?.message === "popup_blocked") {
          toast.warn("Popup blocked. Please allow popups for this site.");
        } else {
          toast.error("Failed to open report.");
        }
      }
    },
    [viewReport]
  );

  // ── Generate flow ─────────────────────────────────────────────────────────

  const initiateReportGeneration = async (selections) => {
    setGenerating((n) => n + 1);
    const endpoint = isCategoryAnalysisFlag
      ? '/llm-reporting/generateCategoryAnalysisHTMLReport'
      : '/llm-reporting/generateBrandAuditHTMLReport';
    try {
      const res = await api.post(
        endpoint,
        {
          transactionId,
          userId,
          format: "html",
          type: discoveryType,
          flag: selections.reportType || flag,
          selections,
        }
      );
      const { jobId, comprehensiveJobId } = res.data;
      if (!jobId) throw new Error("No job ID received from server");
      await addJob(jobId, selections, "brand_audit_html");
      if (comprehensiveJobId) {
        await addJob(comprehensiveJobId, selections, "brand_audit_comprehensive_html");
      }
      setPage(1);
      toast.info("Report generation started. Track status in the table below.", {
        autoClose: 5000,
      });
    } catch (err) {
      const msg =
        err.response?.data?.error || err.message || "Failed to start report generation";
      toast.error(msg);
    } finally {
      setGenerating((n) => n - 1);
    }
  };

  const handleGenerate = async (selections) => {
    setShowModal(false);
    const existing = findCompletedJob(selections, false);
    if (existing) {
      setDuplicateDialog({ open: true, existingJob: existing, pendingSelections: selections, pendingType: "html" });
      return;
    }
    await initiateReportGeneration(selections);
  };


  const handleGenerateJson = async (selections) => {
    setGeneratingJson(true);
    try {
      // JSON download file name: _Assessment by default (follows the configurable audit label)
      const suffix = isCategoryAnalysisFlag ? '_CategoryAnalysis' : `_${ENTITY_LABELS.audit.replace(/\s+/g, '_')}`;
      const modeSuffix = selections.isFullExport
        ? `_Full${selections.includeRawData ? '_With_Raw_Data' : '_With_Calculations'}`
        : '_Custom';
      const filename = `${brandName || 'Brand'}${suffix}${modeSuffix}.json`;
      const { userId: reportUserId, customerId: reportCustomerId } = getSharedReportIdentity(user, selectedCustomerId);
      await downloadFile(
        '/llm-reporting/getReportingJson',
        filename,
        {
          transactionId,
          userId: reportUserId,
          type: discoveryType,
          flag,
          selections,
          customer_id: reportCustomerId,
          primaryMarket: selections.markets?.[0] || 'General Market',
          primaryPersona: selections.persona?.[0] || 'General Persona',
          includeBasePrompt: !!user?.is_include_base_prompt,
        }
      );
      setShowModal(false);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to download JSON';
      toast.error(msg);
    } finally {
      setGeneratingJson(false);
    }
  };

  const handleDuplicateViewExisting = async () => {
    const { existingJob } = duplicateDialog;
    setDuplicateDialog({ open: false, existingJob: null, pendingSelections: null, pendingType: null });
    await handleViewInline(existingJob.jobId);
  };

  const handleDuplicateRegenerate = async () => {
    const { pendingSelections } = duplicateDialog;
    setDuplicateDialog({ open: false, existingJob: null, pendingSelections: null, pendingType: null });
    await initiateReportGeneration(pendingSelections);
  };

  // ── in-progress badge ─────────────────────────────────────────────────────

  const inProgressCount = jobs.filter(
    (j) => j.status === "in-progress" || j.status === "pending"
  ).length;

  // ─────────────────────────────────────────────────────────────────────────
  // INLINE VIEWER MODE
  // ─────────────────────────────────────────────────────────────────────────

  if (inlineViewJobId) {
    if (inlineLoading) {
      return (
        <Box className={classes.root}>
          <Box className={classes.centered} style={{ minHeight: 400 }}>
            <CircularProgress />
          </Box>
        </Box>
      );
    }
    return (
      <HTMLReportViewer
        html={inlineHtml}
        jobId={inlineViewJobId}
        transactionId={transactionId}
        userId={userId}
        onBack={handleBackToHistory}
        onSaveUpdate={handleSaveUpdate}
        onSaveNew={handleSaveNew}
      />
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // HISTORY TABLE MODE
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <Box className={classes.root}>
      {/* ── Header row ── */}
      <Box className={classes.headerRow}>
        <Box className={classes.titleArea}>
          <Typography className={classes.title}>Reporting</Typography>
          {inProgressCount > 0 && (
            <Box className={classes.inProgressBadge}>
              <CircularProgress size={10} thickness={5} />
              <span>{inProgressCount} generating…</span>
            </Box>
          )}
        </Box>

        <Box className={classes.actions}>
          <Tooltip title="Generate new HTML report">
            <span>
              <IconButton
                size="small"
                onClick={() => setShowModal(true)}
                disabled={generating > 0}
                className={classes.actionIcon}
              >
                {generating > 0 ? (
                  <CircularProgress size={18} />
                ) : (
                  <AddCircleOutlineIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Refresh">
            <span>
              <IconButton
                size="small"
                onClick={() => { refreshHistory(); setPage(1); }}
                disabled={historyLoading}
                className={classes.actionIcon}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Table ── */}
      <Box className={classes.tableWrapper}>
        {historyLoading && jobs.length === 0 ? (
          <Box className={classes.centered}>
            <CircularProgress />
          </Box>
        ) : jobs.length === 0 ? (
          <Box className={classes.emptyHistory}>
            <Typography className={classes.emptyText}>
              No reports generated yet. Click the{" "}
              <strong>+</strong> button to get started.
            </Typography>
          </Box>
        ) : (
          <CustomizedTables
            headers={
              isCategoryAnalysisFlag
                ? TABLE_HEADERS.filter(h => h.id !== 'type')
                : TABLE_HEADERS.filter(h => h.id !== 'reportType')
            }
            data={pagedJobs}
            elevation={0}
            size="medium"
            render={(item, index) => (
              <HTMLReportHistoryItem
                key={item.jobId}
                item={item}
                index={index + (page - 1) * PAGE_SIZE}
                hideType={isCategoryAnalysisFlag}
                showReportType={isCategoryAnalysisFlag}
                reportModule={isCategoryAnalysisFlag ? "category_analysis" : "brand_audit"}
                isProductAudit={!isCategoryAnalysisFlag && isProductAudit}
                brandName={brandName}
                onDownload={downloadReport}
                onViewInline={handleViewInline}
                onViewInNewTab={handleViewInNewTab}
                onCancel={cancelJob}
              />
            )}
          />
        )}
      </Box>

      {/* ── Pagination ── */}
      {jobs.length > 0 && (
        <Box className={classes.paginationContainer}>
          <Typography className={classes.resultsText}>
            Showing{" "}
            <strong>{Math.min((page - 1) * PAGE_SIZE + 1, jobs.length)}</strong>{" "}
            to <strong>{Math.min(page * PAGE_SIZE, jobs.length)}</strong> of{" "}
            <strong>{jobs.length}</strong> results
          </Typography>
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              className={classes.pagination}
              showFirstButton
              showLastButton
              siblingCount={1}
              boundaryCount={1}
            />
          )}
        </Box>
      )}

      {/* ── Generate modal ── */}
      <SummaryReportModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onGenerate={handleGenerate}
        onGenerateJson={handleGenerateJson}
        loading={generating > 0}
        jsonLoading={generatingJson}
        brandName={brandName}
        categories={categories}
        llms={llms}
        markets={markets}
        persona={personas}
        title="Generate HTML Report"
        hideGenerateButton={hideGenerateButton}
        reportTypeTabs={isCategoryAnalysisFlag ? categoryReportTypeTabs : null}
        flag={flag}
        type={discoveryType}
        availableMetricKeys={availableMetricKeys}
      />

      {/* ── Duplicate dialog ── */}
      <Dialog
        open={duplicateDialog.open}
        onClose={() =>
          setDuplicateDialog({ open: false, existingJob: null, pendingSelections: null, pendingType: null })
        }
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Report Already Exists</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            A completed HTML report already exists for these parameters
            (Category:{" "}
            <strong>{duplicateDialog.pendingSelections?.category}</strong>).
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Would you like to view the existing report, or generate a new one?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() =>
              setDuplicateDialog({
                open: false,
                existingJob: null,
                pendingSelections: null,
                pendingType: null,
              })
            }
          >
            Cancel
          </Button>
          <Button variant="outlined" onClick={handleDuplicateViewExisting}>
            View Existing
          </Button>
          <Button variant="contained" onClick={handleDuplicateRegenerate}>
            Regenerate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
